import { useId, useSyncExternalStore } from 'react'
import { useSelector } from 'react-redux'

import FeatureEnterpriseTag from './FeatureEnterpriseTag'
import { BooleanSettingConfig, BooleanSettingType } from './utils'

import styles from './styles.module.scss'

import InfoIcon from '@/assets/icons/info.svg?react'
import { Switch } from '@/components/Switch'
import Tooltip from '@/components/Tooltip'
import { getImpersonation, subscribeImpersonation } from '@/lib/impersonation'
import { selectCurrentEnterprise, selectPlanType } from '@/store/features/user/userSlice'
import { ACTIVE_ENTERPRISE_SUBSCRIPTION_STATUSES, EnterpriseSettings, PlanType } from '@/types'

interface SettingItemProps {
  config: BooleanSettingConfig
  overrideLabel?: string
  labelTooltip?: string
  labelDescription?: React.ReactNode
  optimisticSettings: EnterpriseSettings
  toggleSettings: (key: BooleanSettingType, checked: boolean) => void
  defaultValue?: boolean
  description?: string
  disabled?: boolean
  isAdmin: boolean
  /** Suppress the Enterprise upgrade pill on enterprise-locked rows; the disabled toggle's tooltip still explains. */
  hideEnterpriseTag?: boolean
}

const SettingItem = (props: SettingItemProps) => {
  const {
    config,
    optimisticSettings,
    toggleSettings,
    overrideLabel,
    labelTooltip,
    labelDescription,
    defaultValue = false,
    description,
    disabled,
    isAdmin,
    hideEnterpriseTag = false,
  } = props
  const planType = useSelector(selectPlanType)
  const enterprise = useSelector(selectCurrentEnterprise)
  const impersonating = useSyncExternalStore(subscribeImpersonation, getImpersonation)
  const descriptionId = useId()
  const labelTooltipId = useId()

  const settingValue = optimisticSettings[config.key] ?? defaultValue
  const planLockLifted = config.offNeedsNoPlan === true && settingValue === true
  const enterpriseLocked = config.enterpriseOnly && planType !== PlanType.Enterprise && !planLockLifted
  const hasActiveSubscription =
    enterprise?.subscription && ACTIVE_ENTERPRISE_SUBSCRIPTION_STATUSES.includes(enterprise.subscription.status)
  const subscriptionRequired = config.requiresSubscription && !hasActiveSubscription && !planLockLifted
  const label = overrideLabel || config.label
  // View as Admin shows only what the customer can use, without upgrade prompts.
  const hiddenForImpersonation = Boolean(impersonating) && (enterpriseLocked || subscriptionRequired)

  const isDisabled = enterpriseLocked || !isAdmin || subscriptionRequired || disabled

  const tooltipText = subscriptionRequired
    ? 'An active subscription is required to modify this setting'
    : enterpriseLocked
      ? 'Upgrade to Enterprise plan to modify this'
      : !isAdmin
        ? 'Only admins can modify this'
        : undefined

  const labelNode = (
    <div className={styles.toggleLabel}>
      {description ? (
        <div className={styles.settingLabelText}>
          <span>{label}</span>
          <span id={descriptionId} className={styles.settingHelperText}>
            {description}
          </span>
        </div>
      ) : (
        label
      )}
      {labelTooltip && (
        <Tooltip
          text={
            <span id={labelTooltipId} role="tooltip">
              {labelTooltip}
            </span>
          }
          placement="top"
          hideArrow
        >
          <span
            className={styles.infoTooltipTrigger}
            role="img"
            aria-label={`About ${overrideLabel || config.label}`}
            aria-describedby={labelTooltipId}
          >
            <InfoIcon className={styles.infoIcon} aria-hidden="true" />
          </span>
        </Tooltip>
      )}
    </div>
  )

  if (hiddenForImpersonation) return null

  return (
    <div className={`${styles.settingsItem} ${labelDescription ? styles.alignTop : ''}`} key={config.key}>
      {labelDescription ? (
        <div className={styles.labelBlock}>
          {labelNode}
          <div className={styles.labelDescription}>{labelDescription}</div>
        </div>
      ) : (
        labelNode
      )}
      <Tooltip text={tooltipText} placement="top" hideArrow>
        <Switch
          checked={settingValue === true}
          onInputChange={(checked) => toggleSettings(config.key, checked)}
          disabled={isDisabled}
          aria-label={label}
          aria-describedby={description ? descriptionId : undefined}
        />
      </Tooltip>
      {enterpriseLocked && !hideEnterpriseTag && <FeatureEnterpriseTag />}
    </div>
  )
}

export default SettingItem
